import React from 'react';
import { Card, CardBody, CardHeader } from '@heroui/react';
import { motion } from 'framer-motion';

interface DistributionItem {
  label: string;
  value: number;
  color: string;
}

interface DistributionChartProps {
  title: string;
  data: DistributionItem[];
  delay?: number;
}

const DistributionChart: React.FC<DistributionChartProps> = ({ title, data, delay = 0 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay }}>
      <Card className="h-full">
        <CardHeader className="pb-2">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        </CardHeader>
        <CardBody className="pt-2">
          {/* Horizontal bar representation */}
          <div className="h-8 bg-default-100 rounded-full overflow-hidden flex mb-4">
            {data.map((item, index) => {
              const width = total > 0 ? (item.value / total) * 100 : 0;
              if (width === 0) return null;
              return (
                <motion.div
                  key={item.label}
                  className={`h-full ${item.color}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${width}%` }}
                  transition={{ duration: 0.6, delay: delay + index * 0.1 }}
                />
              );
            })}
          </div>

          {/* Legend */}
          <div className="grid grid-cols-2 gap-2">
            {data.map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${item.color}`} />
                <span className="text-sm text-default-600">{item.label}</span>
                <span className="text-sm font-medium text-foreground ml-auto">{item.value}</span>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
};

export default DistributionChart;

